<script lang="ts">
  /**
   * GlobalSearch — глобальный поиск по ⌘K / Ctrl+K.
   *
   * Открывается клавишей из любого места приложения (слушатель на window),
   * ищет по FTS (GET /bookmarks?q=...&limit=8) и переходит на деталь
   * закладки (Enter) или открывает список с поиском (Shift+Enter).
   *
   * Клавиатура: ⌘K открыть, Esc закрыть, ↑/↓ навигация, Enter переход.
   */
  import { _ } from 'svelte-i18n'
  import { goto } from '$app/navigation'
  import { listBookmarks } from '$api/client'
  import type { Bookmark } from '$api/types'
  import { getFaviconUrl, getFolderPath } from '$utils/format'
  import { onMount } from 'svelte'

  let open = $state(false)
  let query = $state('')
  let results = $state<Bookmark[]>([])
  let loading = $state(false)
  let activeIndex = $state(0)
  let error = $state(false)
  let inputEl = $state<HTMLInputElement | null>(null)
  let debounce: ReturnType<typeof setTimeout> | null = null

  function openSearch() {
    open = true
    query = ''
    results = []
    error = false
    activeIndex = 0
    // Небольшая задержка: инпут монтируется вместе с модалкой
    setTimeout(() => inputEl?.focus(), 30)
  }

  function closeSearch() {
    open = false
    if (debounce) clearTimeout(debounce)
  }

  async function search(value: string) {
    if (!value.trim()) {
      results = []
      error = false
      return
    }
    loading = true
    error = false
    try {
      const { items } = await listBookmarks({ q: value.trim(), limit: 8 })
      results = items
      activeIndex = 0
    } catch {
      results = []
      error = true
    } finally {
      loading = false
    }
  }

  function handleInput(value: string) {
    query = value
    if (debounce) clearTimeout(debounce)
    debounce = setTimeout(() => search(value), 250)
  }

  function goTo(index: number) {
    const item = results[index]
    if (!item) return
    closeSearch()
    goto(`/bookmarks/${item.id}`)
  }

  function handleKeydown(e: KeyboardEvent) {
    // Глобальный хоткей: ⌘K / Ctrl+K
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault()
      if (open) {
        closeSearch()
      } else {
        openSearch()
      }
      return
    }
    if (!open) return

    if (e.key === 'Escape') {
      closeSearch()
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      activeIndex = Math.min(activeIndex + 1, results.length - 1)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      activeIndex = Math.max(activeIndex - 1, 0)
    } else if (e.key === 'Enter') {
      e.preventDefault()
      goTo(activeIndex)
    }
  }

  // Закрытие по клику вне диалога (без onclick на оверлее — a11y-чисто)
  function handleDocumentClick(e: MouseEvent) {
    if (open && e.target instanceof Element && !e.target.closest('.search-dialog')) {
      closeSearch()
    }
  }

  onMount(() => {
    document.addEventListener('click', handleDocumentClick)
    return () => document.removeEventListener('click', handleDocumentClick)
  })
</script>

<svelte:window onkeydown={handleKeydown} />

{#if open}
  <div class="search-overlay">
    <div class="search-dialog" role="dialog" aria-modal="true" aria-label={$_('search.global')}>
      <div class="search-head">
        <svg
          class="search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="8"></circle>
          <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
        </svg>
        <input
          bind:this={inputEl}
          class="search-input"
          type="search"
          placeholder={$_('search.placeholder')}
          value={query}
          oninput={(e) => handleInput((e.currentTarget as HTMLInputElement).value)}
          aria-label={$_('search.placeholder')}
        />
        <kbd class="esc-hint">Esc</kbd>
      </div>

      <div class="search-body">
        {#if loading}
          <div class="search-state">{$_('search.loading')}</div>
        {:else if error}
          <div class="search-state search-error">{$_('error.generic')}</div>
        {:else if results.length === 0}
          <div class="search-state">{$_('search.noResults')}</div>
        {:else}
          <ul class="search-results">
            {#each results as item, i (item.id)}
              <li>
                <button
                  type="button"
                  class="result-item"
                  class:active={i === activeIndex}
                  onmouseenter={() => (activeIndex = i)}
                  onclick={() => goTo(i)}
                >
                  <img class="favicon" src={getFaviconUrl(item.url_raw, 32)} alt="" loading="lazy" />
                  <span class="result-main">
                    <span class="result-title">{item.title ?? item.url_normalized}</span>
                    <span class="result-meta">
                      {#if getFolderPath(item.extra_json)}
                        📁 {getFolderPath(item.extra_json)}
                      {/if}
                      <span class="result-url mono">{item.url_normalized.slice(0, 60)}</span>
                    </span>
                  </span>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>

      <footer class="search-foot">
        <span><kbd>↑</kbd> <kbd>↓</kbd> {$_('search.navigate')}</span>
        <span><kbd>Enter</kbd> {$_('search.open')}</span>
      </footer>
    </div>
  </div>
{/if}

<style>
  .search-overlay {
    position: fixed;
    inset: 0;
    z-index: 1500;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 12vh var(--space-4) var(--space-4);
    background: rgba(0, 0, 0, 0.5);
  }

  .search-dialog {
    width: 100%;
    max-width: 560px;
    max-height: 70vh;
    display: flex;
    flex-direction: column;
    background: var(--color-bg-primary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-xl);
    overflow: hidden;
  }

  .search-head {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    border-bottom: 1px solid var(--color-border);
  }

  .search-icon {
    flex-shrink: 0;
    color: var(--color-text-tertiary);
  }

  .search-input {
    flex: 1;
    min-width: 0;
    height: 32px;
    border: none;
    outline: none;
    background: transparent;
    font-family: var(--font-sans);
    font-size: var(--text-base);
    color: var(--color-text-primary);
  }

  .search-input::placeholder {
    color: var(--color-text-tertiary);
  }

  .esc-hint {
    flex-shrink: 0;
    padding: 2px 8px;
    font-family: var(--font-mono);
    font-size: 11px;
    color: var(--color-text-tertiary);
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
  }

  .search-body {
    flex: 1;
    overflow-y: auto;
    min-height: 120px;
    max-height: 50vh;
  }

  .search-state {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-8) var(--space-4);
    font-size: var(--text-sm);
    color: var(--color-text-tertiary);
    text-align: center;
  }

  .search-error {
    color: var(--color-danger);
  }

  .search-results {
    list-style: none;
    padding: var(--space-2);
  }

  .result-item {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    padding: var(--space-2) var(--space-3);
    border: none;
    background: transparent;
    border-radius: var(--radius-md);
    cursor: pointer;
    text-align: left;
  }

  .result-item.active,
  .result-item:hover {
    background: var(--color-accent-light);
  }

  .favicon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    border-radius: 3px;
  }

  .result-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .result-title {
    font-size: var(--text-sm);
    font-weight: 500;
    color: var(--color-text-primary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .result-meta {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: 12px;
    color: var(--color-text-tertiary);
    overflow: hidden;
    white-space: nowrap;
  }

  .result-url {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-foot {
    display: flex;
    align-items: center;
    gap: var(--space-4);
    padding: var(--space-2) var(--space-4);
    border-top: 1px solid var(--color-border);
    font-size: 11px;
    color: var(--color-text-tertiary);
  }

  .search-foot kbd {
    padding: 1px 5px;
    font-family: var(--font-mono);
    font-size: 10px;
    color: var(--color-text-secondary);
    background: var(--color-bg-tertiary);
    border: 1px solid var(--color-border);
    border-radius: var(--radius-sm);
  }

  @media (max-width: 767px) {
    .search-overlay {
      padding: 8vh var(--space-3) var(--space-3);
    }
  }
</style>
